import { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import * as mediasoupClient from 'mediasoup-client';
import { ENV } from '../config/env';
import { getExamToken } from '../auth/examToken';
import { allowStarvationReload, resetStarvationReloads } from '../utils/starvationReloadGuard';

// ---------------- MOBILE DETECTION UTILITY ----------------
const isMobileDevice = () => {
  // Check user agent for mobile devices
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());

  // Check screen size (mobile typically < 768px width)
  const isSmallScreen = window.innerWidth < 768;

  // Check for touch support (most mobile devices have touch)
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Consider it mobile if user agent matches OR (small screen AND touch support)
  return isMobileUA || (isSmallScreen && hasTouchScreen);
};

const CAMERA_ENCODINGS = [{ maxBitrate: 100000, maxFramerate: 10 }];
const SCREEN_ENCODINGS = [{ maxBitrate: 200000, maxFramerate: 5 }];
const SIGNAL_TIMEOUT_MS = 15000;
const ICE_RESTART_MIN_INTERVAL_MS = 10000;
const ICE_DISCONNECTED_GRACE_MS = 3000;

const isLiveTrack = (t) => !!t && t.readyState === 'live';

// policy: optional (descriptor) => boolean. No policy = consume everything (students).
const evaluatePolicy = (policy, descriptor) => {
  if (!policy) return true;
  try {
    return !!policy(descriptor);
  } catch (err) {
    console.error('Consumer policy threw; defaulting to consume', err);
    return true;
  }
};

const useMediasoup = (roomId, userId, userType, userName, registrationId, fullName, assessmentId) => {
  // ---------------- STATE MANAGEMENT ----------------
  const [isConnected, setIsConnected] = useState(false);
  const [hasJoinedRoom, setHasJoinedRoom] = useState(false);

  // Changed from a single localStream to an array for multiple cameras
  const [localStreams, setLocalStreams] = useState([]);
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [roomUsers, setRoomUsers] = useState([]);
  const [error, setError] = useState(null);
  const [mediaConnectivityError, setMediaConnectivityError] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showScreenShareButton, setShowScreenShareButton] = useState(false);

  // Mobile: set once on mount so resizing to mobile view doesn't flip behavior (avoids timer going to 0 and recording get/part/end)
  const [isMobileDeviceStable] = useState(() => isMobileDevice());

  // Clear error message after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  // ---------------- REFS ----------------
  const socketRef = useRef(null);
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);

  // consumersRef: producerId -> Consumer. producersRef: kindKey ('video-1' | 'video-2' | 'audio' | 'screen' ...) -> Producer
  const consumersRef = useRef(new Map());
  const producersRef = useRef(new Map());

  // Refs for local previews (supporting up to 2 cameras)
  const localVideoRef1 = useRef(null);
  const localVideoRef2 = useRef(null);

  // 🔹 QUEUE: Stores producers that arrive before the transport is ready
  const consumerQueue = useRef([]);

  // Local media we intend to publish: kindKey -> { track, encodings, appData, heartbeat }.
  // Survives socket reconnects so every local track (camera, mic, screen) is re-produced on the new transport.
  const localProducerSpecsRef = useRef(new Map());
  const producingRef = useRef(new Map()); // kindKey -> in-flight produce promise
  const localStreamsRef = useRef([]);
  const lastCameraCountRef = useRef(2);
  const startingLocalVideoRef = useRef(null);
  const setupInProgressRef = useRef(false);

  // Remote producers this client may consume: producerId -> { producerId, userId, userType, userName, kind }
  const availableProducersRef = useRef(new Map());
  const pendingConsumesRef = useRef(new Map()); // producerId -> promise
  const pendingConsumeKindsRef = useRef(new Map()); // producerId -> { kind, remoteUserType } while in flight
  // Optional predicate (consumer descriptor) => boolean. null = consume and play everything (students).
  const consumerPolicyRef = useRef(null);

  const sessionReplacedRef = useRef(false);
  const authFailedRef = useRef(false);
  const unmountedRef = useRef(false);
  // Incremented on every connect/disconnect so async setup started on a previous connection can detect staleness.
  const connGenRef = useRef(0);

  // Stable references to the latest function implementations (used from socket handlers registered once)
  const consumeProducerRef = useRef(null);
  const setupMediasoupRef = useRef(null);
  const startLocalVideoRef = useRef(null);
  const applyConsumerPolicyRef = useRef(null);

  // ---------------- SERVER URL (from ENV config) ----------------
  const getServerUrl = () => ENV.SOCKET_URL;

  // Emit with acknowledgement; rejects on { error } or timeout so callers never hang on a dead socket.
  const request = useCallback((event, data) => new Promise((resolve, reject) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      reject(new Error(`socket not connected (${event})`));
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`signaling timeout (${event})`));
    }, SIGNAL_TIMEOUT_MS);
    const ack = (res) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (res && res.error) reject(new Error(res.error));
      else resolve(res || {});
    };
    if (data === undefined) socket.emit(event, ack);
    else socket.emit(event, data, ack);
  }), []);

  // Students consume staff media only. Staff consume student media only.
  const isConsumableProducer = useCallback((p) => {
    if (!p) return false;
    if (userType === 'student') return p.userType === 'staff';
    if (userType === 'staff') return p.userType === 'student';
    return false;
  }, [userType]);

  // ---------------- CRITICAL: CLEANUP FUNCTION ----------------
  // Closes transports/consumers/producers. Local tracks are kept alive (producers use stopTracks:false)
  // so they can be re-produced after reconnect. Pass { stopLocalTracks: true } only on intentional exit.
  const cleanupMediasoup = useCallback(({ stopLocalTracks = false } = {}) => {
    console.log(`🧹 Cleaning up Mediasoup resources (stopLocalTracks=${stopLocalTracks})...`);

    if (sendTransportRef.current) {
      try { sendTransportRef.current.close(); } catch (e) { console.warn('send transport close failed', e); }
      sendTransportRef.current = null;
    }
    if (recvTransportRef.current) {
      try { recvTransportRef.current.close(); } catch (e) { console.warn('recv transport close failed', e); }
      recvTransportRef.current = null;
    }

    consumersRef.current.forEach(c => { if (!c.closed) c.close(); });
    consumersRef.current.clear();

    producersRef.current.forEach(p => { if (!p.closed) p.close(); });
    producersRef.current.clear();
    producingRef.current.clear();

    consumerQueue.current = [];
    pendingConsumesRef.current.clear();
    pendingConsumeKindsRef.current.clear();
    availableProducersRef.current.clear();

    deviceRef.current = null;
    setupInProgressRef.current = false;

    setRemoteStreams([]);

    if (stopLocalTracks) {
      localProducerSpecsRef.current.forEach(spec => {
        try { spec.track.stop(); } catch (_) { /* already stopped */ }
      });
      localProducerSpecsRef.current.clear();
      localStreamsRef.current.forEach(s => {
        const stream = s?.stream || s;
        if (stream && stream.getTracks) stream.getTracks().forEach(t => t.stop());
      });
      localStreamsRef.current = [];
      if (!unmountedRef.current) {
        setLocalStreams([]);
        setIsScreenSharing(false);
      }
    }
  }, []);

  // ---------------- CONSUMER PAUSE / RESUME (server-side, so paused consumers carry no RTP) ----------------
  const setConsumerPaused = useCallback(async (consumer, paused) => {
    if (!consumer || consumer.closed) return;
    if (!!consumer.appData.serverPaused === paused) return;
    consumer.appData.serverPaused = paused;
    try {
      await request(paused ? 'pause-consumer' : 'resume-consumer', { consumerId: consumer.id });
      if (consumer.closed) return;
      if (paused) consumer.pause(); else consumer.resume();
    } catch (err) {
      // Revert so the next policy pass retries
      consumer.appData.serverPaused = !paused;
      console.warn(`⚠️ ${paused ? 'pause' : 'resume'}-consumer failed for ${consumer.appData.kind}:`, err.message);
    }
  }, [request]);

  const wantsConsumer = (descriptor) => evaluatePolicy(consumerPolicyRef.current, descriptor);

  // The exam room shows one proctor stream per kind, so a student consumes at most one staff producer per kind;
  // the next available one is consumed when it closes (applyConsumerPolicy after producer/consumer close).
  const staffKindTaken = (producerId, kind) => {
    for (const [pid, c] of consumersRef.current) {
      if (pid !== producerId && !c.closed && c.appData.remoteUserType === 'staff' && c.appData.kind === kind) return true;
    }
    for (const [pid, p] of pendingConsumeKindsRef.current) {
      if (pid !== producerId && p.remoteUserType === 'staff' && p.kind === kind) return true;
    }
    return false;
  };

  const closeConsumer = useCallback((producerId, { notifyServer = true } = {}) => {
    const consumer = consumersRef.current.get(producerId);
    if (!consumer) return;
    consumersRef.current.delete(producerId);
    if (!consumer.closed) consumer.close();
    if (notifyServer && socketRef.current?.connected) {
      socketRef.current.emit('close-consumer', { consumerId: consumer.id });
    }
    setRemoteStreams(prev => prev.filter(s => s.producerId !== producerId));
  }, []);

  // Pause/resume existing consumers and consume newly wanted producers according to the current policy.
  const applyConsumerPolicy = useCallback(() => {
    const policy = consumerPolicyRef.current;
    consumersRef.current.forEach((consumer) => {
      if (consumer.closed) return;
      const want = evaluatePolicy(policy, {
        remoteUserId: consumer.appData.remoteUserId,
        remoteUserType: consumer.appData.remoteUserType,
        kind: consumer.appData.kind,
        isMobile: !!consumer.appData.isMobile,
      });
      setConsumerPaused(consumer, !want);
    });
    availableProducersRef.current.forEach((p) => {
      if (consumersRef.current.has(p.producerId)) return;
      if (!evaluatePolicy(policy, { remoteUserId: p.userId, remoteUserType: p.userType, kind: p.kind, isMobile: !!p.isMobile })) return;
      consumeProducerRef.current?.(p.producerId, p.userId, p.userType, p.userName, p.kind);
    });
  }, [setConsumerPaused]);
  applyConsumerPolicyRef.current = applyConsumerPolicy;

  const setConsumerPolicy = useCallback((policyFn) => {
    consumerPolicyRef.current = typeof policyFn === 'function' ? policyFn : null;
    applyConsumerPolicyRef.current?.();
  }, []);

  const rememberProducer = useCallback((p) => {
    if (!p || !p.producerId || !isConsumableProducer(p)) return false;
    availableProducersRef.current.set(p.producerId, {
      producerId: p.producerId, userId: p.userId, userType: p.userType, userName: p.userName, kind: p.kind, isMobile: !!p.isMobile,
    });
    return true;
  }, [isConsumableProducer]);

  // forUserId asks the server for that user's producers only (avoids full-room lists on every join).
  const syncProducers = useCallback(async (filterFn, forUserId) => {
    try {
      const { producers = [] } = await request('get-producers', forUserId != null ? { userId: String(forUserId) } : undefined);
      producers
        .filter(p => (!filterFn || filterFn(p)) && rememberProducer(p))
        .forEach(p => {
          if (evaluatePolicy(consumerPolicyRef.current, { remoteUserId: p.userId, remoteUserType: p.userType, kind: p.kind, isMobile: !!p.isMobile })) {
            consumeProducerRef.current?.(p.producerId, p.userId, p.userType, p.userName, p.kind);
          }
        });
    } catch (err) {
      console.warn('get-producers failed:', err.message);
    }
  }, [request, rememberProducer]);

  // ---------------- INITIALIZATION EFFECT ----------------
  useEffect(() => {
    if (!roomId || !userId) return;
    unmountedRef.current = false;
    sessionReplacedRef.current = false;
    authFailedRef.current = false;

    const initialize = async () => {
      try {
        if (!socketRef.current) {
          socketRef.current = io(getServerUrl(), {
            transports: ['polling', 'websocket'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
            forceNew: true,
            // Function form so every reconnect sends the current token
            auth: (cb) => cb({ token: getExamToken() }),
          });

          // --- SOCKET EVENT LISTENERS ---

          socketRef.current.on('connect', () => {
            console.log('✅ Socket connected successfully');
            connGenRef.current += 1;
            setIsConnected(true);
            // Identity is taken from the signed token on the server; these fields remain for backward compatibility.
            socketRef.current.emit('join-room', { roomId, userId, userType, userName, registrationId, fullName, isMobile: isMobileDeviceStable, assessment_id: assessmentId });
          });

          socketRef.current.on('disconnect', (reason) => {
            console.warn(`❌ Socket disconnected: ${reason}`);
            connGenRef.current += 1;
            setIsConnected(false);
            setHasJoinedRoom(false);
            // Temporary disconnect: release WebRTC objects, keep local tracks for re-produce on reconnect.
            cleanupMediasoup();
            // The client does not auto-reconnect after a server-initiated disconnect; do it unless we were replaced.
            if (reason === 'io server disconnect' && !sessionReplacedRef.current && !authFailedRef.current && !unmountedRef.current) {
              setTimeout(() => {
                if (socketRef.current && !socketRef.current.connected && !unmountedRef.current) socketRef.current.connect();
              }, 1000);
            }
          });

          socketRef.current.on('connect_error', (err) => {
            const msg = err?.message || '';
            console.error('Socket connection error:', msg);
            if (msg.startsWith('unauthorized')) {
              authFailedRef.current = true;
              setError('Your exam session link is invalid or has expired. Please re-open the exam from the portal.');
            } else {
              setError('Failed to connect to signaling server');
            }
          });

          socketRef.current.on('join-error', ({ message } = {}) => {
            console.error('join-room rejected:', message);
            setError(message || 'Unable to join the exam room.');
          });

          // Another tab/device for the same user+device type joined; this connection was evicted by the server.
          socketRef.current.on('session-replaced', () => {
            console.warn('⚠️ Session replaced by a newer connection for the same user/device');
            sessionReplacedRef.current = true;
            cleanupMediasoup({ stopLocalTracks: true });
            setError('This exam was opened in another tab or device. This window has been disconnected.');
          });

          // Trigger Mediasoup setup once joined
          socketRef.current.on('joined-room', async () => {
            console.log('Joined room. Setting up Mediasoup...');
            setHasJoinedRoom(true);

            // Request current room users immediately after joining (staff sees students who joined before them)
            socketRef.current.emit('get-room-users', (res) => {
              if (res && res.users) {
                console.log(`📋 Received ${res.users.length} existing users in room`);
                setRoomUsers(res.users);
              }
            });

            await setupMediasoupRef.current?.();
          });

          // 🔹 NEW PRODUCER HANDLER (Auto-Consume Logic)
          socketRef.current.on('new-producer', (p) => {
            const { producerId, userId: remoteUserId, userType: remoteUserType, userName: remoteUserName, kind } = p || {};
            if (!rememberProducer(p)) {
              console.log(`Ignoring ${kind} from ${remoteUserName} (role mismatch)`);
              return;
            }
            if (!evaluatePolicy(consumerPolicyRef.current, { remoteUserId, remoteUserType, kind, isMobile: !!p.isMobile })) {
              console.log(`🔔 ${kind} from ${remoteUserName} available (not consumed until needed)`);
              return;
            }
            console.log(`🔔 New Producer: consuming ${kind} from ${remoteUserName} (${remoteUserType})`);
            consumeProducerRef.current?.(producerId, remoteUserId, remoteUserType, remoteUserName, kind);
          });

          // 🔹 PRODUCER CLOSED HANDLER
          socketRef.current.on('producer-closed', ({ producerId, userId: closedUserId, kind } = {}) => {
            console.log(`Producer closed for User: ${closedUserId}, Kind: ${kind}`);

            if (producerId) {
              availableProducersRef.current.delete(producerId);
              closeConsumer(producerId, { notifyServer: false });
              if (userType === 'student') applyConsumerPolicyRef.current?.();
              return;
            }

            // Legacy payload without producerId: match by user + kind
            setRemoteStreams((prev) => prev.filter((s) => {
              if (kind) return !(s.userId === closedUserId && s.kind === kind);
              return s.userId !== closedUserId;
            }));
            consumersRef.current.forEach((consumer, key) => {
              if (consumer.appData.remoteUserId === closedUserId && (!kind || consumer.appData.kind === kind)) {
                if (!consumer.closed) consumer.close();
                consumersRef.current.delete(key);
              }
            });
            availableProducersRef.current.forEach((ap, key) => {
              if (ap.userId === closedUserId && (!kind || ap.kind === kind)) availableProducersRef.current.delete(key);
            });
          });

          // Server closed our consumer (e.g. its producer went away)
          socketRef.current.on('consumer-closed', ({ producerId } = {}) => {
            if (producerId) {
              availableProducersRef.current.delete(producerId);
              closeConsumer(producerId, { notifyServer: false });
              if (userType === 'student') applyConsumerPolicyRef.current?.();
            }
          });

          // 🔹 USER LEFT HANDLER
          // Streams are removed by producer-closed (per producerId); only the user list is updated here.
          socketRef.current.on('user-left', ({ userId: leftUserId, isMobile: leftIsMobile, peerId } = {}) => {
            console.log(`User left: ${leftUserId} (isMobile: ${leftIsMobile})`);
            setRoomUsers(prev => prev.filter(u => {
              if (peerId && u.peerId) return u.peerId !== peerId;
              if (leftIsMobile !== undefined) return !(u.userId === leftUserId && !!u.isMobile === !!leftIsMobile);
              return u.userId !== leftUserId;
            }));
          });

          // 🔹 USER LIST UPDATES
          socketRef.current.on('room-users', ({ users }) => {
            setRoomUsers(users || []);
          });

          socketRef.current.on('user-joined', (user) => {
            console.log(`👤 User joined: ${user.userName} (${user.userType}, isMobile: ${user.isMobile})`);
            setRoomUsers(prev => {
              const sameSlot = (u) => u.userId === user.userId && !!u.isMobile === !!user.isMobile;
              if (prev.some(sameSlot)) return prev.map(u => (sameSlot(u) ? { ...u, ...user } : u));
              return [...prev, user];
            });

            // Safety net: new producers normally arrive via 'new-producer'; re-sync once in case one was missed.
            if (isConsumableProducer({ userType: user.userType })) {
              setTimeout(() => {
                if (recvTransportRef.current && deviceRef.current) {
                  syncProducers(p => p.userId === user.userId, user.userId);
                }
              }, 3000);
            }
          });

          // Presence flags (isVerified / isEntered / hasCamera) changed for a peer we can see.
          socketRef.current.on('user-updated', (user) => {
            if (!user) return;
            console.log(`👤 User updated: ${user.userName || user.userId} (isMobile: ${user.isMobile}, isVerified: ${user.isVerified}, isEntered: ${user.isEntered}, hasCamera: ${user.hasCamera})`);
            setRoomUsers(prev => {
              const sameSlot = (u) => u.userId === user.userId && !!u.isMobile === !!user.isMobile;
              if (prev.some(sameSlot)) return prev.map(u => (sameSlot(u) ? { ...u, ...user } : u));
              return [...prev, user];
            });
          });
        }
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message);
      }
    };

    initialize();

    return () => {
      unmountedRef.current = true;
      if (socketRef.current) {
        console.log("Cleaning up socket connection...");
        // Intentional exit: release camera, mic and screen
        cleanupMediasoup({ stopLocalTracks: true });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [roomId, userId, userType, userName, registrationId, fullName, assessmentId, isMobileDeviceStable,
    cleanupMediasoup, rememberProducer, closeConsumer, isConsumableProducer, syncProducers]);

  // ---------------- CORE FUNCTION: CONSUME PRODUCER ----------------
  const consumeProducer = async (producerId, remoteUserId, remoteUserType, remoteUserName, kind) => {
    if (!producerId) return;
    const isMobile = !!availableProducersRef.current.get(producerId)?.isMobile;

    // 1. Already consuming: make sure UI state is in sync and the pause state follows the policy
    const existing = consumersRef.current.get(producerId);
    if (existing && !existing.closed) {
      setRemoteStreams((prev) => {
        if (prev.some(s => s.producerId === producerId)) return prev;
        console.warn(`⚠️ UI out of sync. Restoring ${kind}...`);
        const stream = new MediaStream([existing.track]);
        return [
          ...prev.filter((s) => !(s.userId === remoteUserId && s.kind === kind)),
          { userId: remoteUserId, userType: remoteUserType, userName: remoteUserName, kind, stream, producerId, consumerId: existing.id },
        ];
      });
      setConsumerPaused(existing, !wantsConsumer({ remoteUserId, remoteUserType, kind, isMobile }));
      return;
    }

    // 2. Deduplicate concurrent consume calls for the same producer
    if (pendingConsumesRef.current.has(producerId)) return pendingConsumesRef.current.get(producerId);

    if (userType === 'student' && remoteUserType === 'staff' && staffKindTaken(producerId, kind)) return;

    // 3. Transport not ready: queue it
    if (!recvTransportRef.current || !deviceRef.current) {
      if (!consumerQueue.current.some(item => item.producerId === producerId)) {
        consumerQueue.current.push({ producerId, remoteUserId, remoteUserType, remoteUserName, kind });
      }
      return;
    }

    const transport = recvTransportRef.current;
    const run = (async () => {
      try {
        const { params } = await request('consume', {
          transportId: transport.id,
          producerId,
          rtpCapabilities: deviceRef.current.rtpCapabilities,
        });
        if (transport.closed || transport !== recvTransportRef.current) return;

        const consumer = await transport.consume(params);
        // Server creates consumers paused; serverPaused tracks that state.
        consumer.appData = { remoteUserId, remoteUserType, remoteUserName, kind, producerId, isMobile, serverPaused: true };
        consumersRef.current.set(producerId, consumer);

        consumer.on('transportclose', () => {
          consumersRef.current.delete(producerId);
        });

        const { track } = consumer;
        const stream = new MediaStream([track]);
        console.log(`🎥 Stream Active: ${kind} from ${remoteUserName}`);

        setRemoteStreams((prev) => [
          ...prev.filter((s) => s.producerId !== producerId && !(s.userId === remoteUserId && s.kind === kind)),
          { userId: remoteUserId, userType: remoteUserType, userName: remoteUserName, kind, stream, producerId, consumerId: consumer.id },
        ]);

        await setConsumerPaused(consumer, !wantsConsumer({ remoteUserId, remoteUserType, kind, isMobile }));

        track.onended = () => {
          console.log(`⚠️ Track ended unexpectedly for ${kind} from ${remoteUserName}`);
          closeConsumer(producerId);
          // Re-subscribe after a short delay (recovers after phone call interruptions / brief network loss)
          setTimeout(() => {
            if (socketRef.current && recvTransportRef.current && deviceRef.current) {
              syncProducers(p => p.userId === remoteUserId && p.kind === kind, remoteUserId);
            }
          }, 2500);
        };
      } catch (err) {
        console.error(`Error consuming ${kind}:`, err.message || err);
        if (/not found/i.test(err.message || '')) {
          availableProducersRef.current.delete(producerId);
          if (userType === 'student') setTimeout(() => applyConsumerPolicyRef.current?.(), 0);
        }
      }
    })();

    pendingConsumesRef.current.set(producerId, run);
    pendingConsumeKindsRef.current.set(producerId, { kind, remoteUserType });
    try {
      await run;
    } finally {
      pendingConsumesRef.current.delete(producerId);
      pendingConsumeKindsRef.current.delete(producerId);
    }
  };

  consumeProducerRef.current = consumeProducer;

  // ---------------- LOCAL PRODUCERS ----------------
  // Anti-cheat heartbeat: reload if a camera producer sends no RTP for ~30s (existing behavior).
  // Reloads are capped per tab; past the cap the student gets a persistent connectivity error instead of an
  // endless reload loop (UDP blocked and no TURN relay).
  const attachHeartbeat = (producer, kindKey) => {
    const heartbeatInterval = setInterval(async () => {
      if (producer.closed) {
        clearInterval(heartbeatInterval);
        return;
      }
      try {
        const stats = await producer.getStats();
        let isSendingBytes = false;
        stats.forEach(report => {
          if (report.type === 'outbound-rtp' && report.bytesSent > 0) isSendingBytes = true;
        });
        if (!isSendingBytes) {
          producer.appData.zeroByteCount = (producer.appData.zeroByteCount || 0) + 1;
          if (producer.appData.zeroByteCount >= 2 && !producer.closed && !producer.appData.starvationReported) {
            if (allowStarvationReload()) {
              console.warn(`🚨 Mediasoup packet starvation detected on ${kindKey}! Possible UDP block or NetLimiter. Refreshing...`);
              window.location.reload();
            } else {
              console.warn(`🚨 Packet starvation on ${kindKey} persists after automatic reloads; not reloading again.`);
              producer.appData.starvationReported = true;
              setMediaConnectivityError(
                'Your camera video cannot reach the exam server — your network or firewall/VPN is blocking it. ' +
                'Switch to another network (for example a mobile hotspot) or turn off VPN/firewall software, then press Retry.'
              );
            }
          }
        } else {
          producer.appData.zeroByteCount = 0;
          if (producer.appData.starvationReported) {
            producer.appData.starvationReported = false;
            setMediaConnectivityError(null);
          }
          resetStarvationReloads();
        }
      } catch (e) {
        console.error("Heartbeat stats error", e);
      }
    }, 15000);
    producer.on('transportclose', () => clearInterval(heartbeatInterval));
  };

  // Single entry point for publishing local media. Records the spec (for re-produce after reconnect),
  // serializes produce per kind, and closes any previous producer of the same kind (no duplicates).
  const produceLocal = useCallback(async (kindKey, track, { encodings, appData = {}, heartbeat = false } = {}) => {
    if (!isLiveTrack(track)) return null;
    localProducerSpecsRef.current.set(kindKey, { track, encodings, appData, heartbeat });

    const inflight = producingRef.current.get(kindKey);
    if (inflight) {
      try { await inflight; } catch (_) { /* previous attempt failed; continue with this one */ }
    }

    const transport = sendTransportRef.current;
    if (!transport || transport.closed) return null; // produced later by reproduceLocalMedia()

    const run = (async () => {
      const spec = localProducerSpecsRef.current.get(kindKey);
      if (!spec || spec.track !== track || !isLiveTrack(track)) return null; // superseded while waiting

      const current = producersRef.current.get(kindKey);
      if (current && !current.closed) {
        if (current.track === track) return current;
        current.close();
        producersRef.current.delete(kindKey);
        socketRef.current?.emit('close-producer', { kind: kindKey, producerId: current.id });
      }

      const producer = await transport.produce({
        track,
        encodings,
        stopTracks: false,
        appData: { ...appData, kind: kindKey },
      });
      if (transport.closed || transport !== sendTransportRef.current) {
        producer.close();
        return null;
      }
      producersRef.current.set(kindKey, producer);
      if (heartbeat) attachHeartbeat(producer, kindKey);
      return producer;
    })();

    producingRef.current.set(kindKey, run);
    try {
      return await run;
    } finally {
      if (producingRef.current.get(kindKey) === run) producingRef.current.delete(kindKey);
    }
  }, []);

  const unpublishLocal = useCallback((kindKey, { stopTrack = false } = {}) => {
    const spec = localProducerSpecsRef.current.get(kindKey);
    localProducerSpecsRef.current.delete(kindKey);
    const producer = producersRef.current.get(kindKey);
    producersRef.current.delete(kindKey);
    if (producer && !producer.closed) producer.close();
    if (stopTrack) {
      if (spec?.track) spec.track.stop();
      if (producer?.track && producer.track !== spec?.track) producer.track.stop();
    }
    if (socketRef.current?.connected) {
      socketRef.current.emit('close-producer', { kind: kindKey, producerId: producer?.id });
    }
  }, []);

  // Re-publish every recorded local track on the (new) send transport. Returns true if a camera track died.
  const reproduceLocalMedia = async () => {
    let cameraLost = false;
    for (const [kindKey, spec] of Array.from(localProducerSpecsRef.current.entries())) {
      if (!isLiveTrack(spec.track)) {
        localProducerSpecsRef.current.delete(kindKey);
        if (kindKey === 'screen') {
          console.warn('Screen-share track ended while disconnected; screen share must be restarted.');
          setIsScreenSharing(false);
        } else if (kindKey !== 'audio') {
          cameraLost = true;
        }
        continue;
      }
      try {
        await produceLocal(kindKey, spec.track, spec);
        console.log(`✅ Re-produced ${kindKey} after (re)connect`);
      } catch (e) {
        console.warn(`⚠️ Re-produce failed for ${kindKey}:`, e.message || e);
      }
    }
    return cameraLost;
  };

  // ---------------- ICE RESTART (transport failure without socket loss) ----------------
  const watchTransport = (transport) => {
    let lastRestart = 0;
    let disconnectedTimer = null;
    const restart = async () => {
      if (transport.closed || Date.now() - lastRestart < ICE_RESTART_MIN_INTERVAL_MS) return;
      lastRestart = Date.now();
      try {
        const { iceParameters } = await request('restart-ice', { transportId: transport.id });
        if (!transport.closed && iceParameters) {
          await transport.restartIce({ iceParameters });
          console.log(`🧊 ICE restarted on ${transport.direction} transport`);
        }
      } catch (err) {
        console.warn(`ICE restart failed on ${transport.direction} transport:`, err.message || err);
      }
    };
    transport.on('connectionstatechange', (state) => {
      if (disconnectedTimer) { clearTimeout(disconnectedTimer); disconnectedTimer = null; }
      if (state === 'failed') restart();
      else if (state === 'disconnected') disconnectedTimer = setTimeout(restart, ICE_DISCONNECTED_GRACE_MS);
    });
  };

  // ---------------- SETUP MEDIASOUP ----------------
  const setupMediasoup = async () => {
    if (deviceRef.current || setupInProgressRef.current) return;
    setupInProgressRef.current = true;
    const genAtStart = connGenRef.current;
    const isStale = () => connGenRef.current !== genAtStart || !socketRef.current?.connected;
    try {
      console.log("Setting up Mediasoup Device...");

      const { rtpCapabilities } = await request('get-router-rtp-capabilities');
      const device = new mediasoupClient.Device();
      await device.load({ routerRtpCapabilities: rtpCapabilities });

      const sendRes = await request('create-transport', { consuming: false, producing: true });
      const sendTransport = device.createSendTransport(sendRes.params);

      sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
        request('connect-transport', { transportId: sendTransport.id, dtlsParameters })
          .then(() => callback())
          .catch(errback);
      });

      sendTransport.on('produce', (parameters, callback, errback) => {
        request('produce', {
          transportId: sendTransport.id,
          kind: parameters.kind,
          rtpParameters: parameters.rtpParameters,
          appData: parameters.appData,
        })
          .then(({ id }) => (id ? callback({ id }) : errback(new Error('produce: server returned no id'))))
          .catch(errback);
      });

      const recvRes = await request('create-transport', { consuming: true, producing: false });
      const recvTransport = device.createRecvTransport(recvRes.params);

      recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
        request('connect-transport', { transportId: recvTransport.id, dtlsParameters })
          .then(() => callback())
          .catch(errback);
      });

      // Socket dropped (or reconnected) while we were negotiating: discard; the next 'joined-room' starts over.
      if (isStale()) {
        sendTransport.close();
        recvTransport.close();
        return;
      }

      watchTransport(sendTransport);
      watchTransport(recvTransport);
      deviceRef.current = device;
      sendTransportRef.current = sendTransport;
      recvTransportRef.current = recvTransport;

      console.log("Transports created. Processing Queue...");

      const queued = consumerQueue.current;
      consumerQueue.current = [];
      for (const item of queued) {
        rememberProducer({ producerId: item.producerId, userId: item.remoteUserId, userType: item.remoteUserType, userName: item.remoteUserName, kind: item.kind });
        if (wantsConsumer({ remoteUserId: item.remoteUserId, remoteUserType: item.remoteUserType, kind: item.kind })) {
          await consumeProducer(item.producerId, item.remoteUserId, item.remoteUserType, item.remoteUserName, item.kind);
        }
      }

      // Re-publish camera / mic / screen that survived the disconnect
      if (localProducerSpecsRef.current.size > 0) {
        console.log('🔄 Re-producing local media after (re)connect...');
        const cameraLost = await reproduceLocalMedia();
        if (cameraLost && startLocalVideoRef.current) {
          console.warn('📷 Camera track ended during disconnect; re-acquiring camera...');
          startLocalVideoRef.current(lastCameraCountRef.current);
        }
      }

      // Fetch existing producers (in case we missed 'new-producer' events during reconnect)
      await syncProducers();
    } catch (err) {
      if (isStale()) {
        console.warn('Mediasoup setup aborted by reconnect:', err.message || err);
      } else if (err.message && (err.message.includes('not available on this server') || err.message.includes('Mediasoup router not available'))) {
        console.info('ℹ️ Media server running in signaling/API mode:', err.message);
      } else {
        console.error('Mediasoup Setup Error:', err);
        setError("Failed to connect to media server. Please refresh.");
      }
    } finally {
      if (connGenRef.current === genAtStart) setupInProgressRef.current = false;
    }
  };
  setupMediasoupRef.current = setupMediasoup;

  // ---------------- START LOCAL WEBCAMS (ROBUST VERSION) ----------------
  // Device-aware camera mapping: Windows = video-1 (Camera-1), Mobile = video-2 (Camera-2)
  const startLocalVideo = async (targetCameraCount = 2) => {
    lastCameraCountRef.current = targetCameraCount;

    // Already running with live camera tracks: nothing to do (prevents duplicates)
    const current = localStreamsRef.current;
    const allLive = current.length > 0 && current.every(s => {
      const stream = s?.stream || s;
      return stream?.getVideoTracks?.().some(isLiveTrack);
    });
    if (allLive) return [];

    // Concurrent callers share one acquisition
    if (startingLocalVideoRef.current) return startingLocalVideoRef.current;

    const run = acquireLocalVideo(targetCameraCount, current);
    startingLocalVideoRef.current = run;
    try {
      return await run;
    } finally {
      startingLocalVideoRef.current = null;
    }
  };
  startLocalVideoRef.current = startLocalVideo;

  const acquireLocalVideo = async (targetCameraCount, previousStreams) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      const msg = 'Camera not available: secure context (HTTPS) and getUserMedia support required.';
      console.error(msg);
      setError(msg);
      return [];
    }

    try {
      console.log("Enumerate devices...");
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => {
        if (d.kind !== 'videoinput') return false;
        const label = d.label.toLowerCase();
        // Anti-cheat: Filter out virtual cameras by device string
        if (label.includes('obs') || label.includes('virtual') || label.includes('manycam') ||
          label.includes('xsplit') || label.includes('camtwist') || label.includes('epoccam') ||
          label.includes('iriun') || label.includes('droidcam') || label.includes('altercam')) {
          console.warn(`🚫 Blocked suspected virtual camera: ${d.label}`);
          return false;
        }
        return true;
      });
      console.log(`Found ${videoDevices.length} physical cameras. Target: ${targetCameraCount}`);

      if (videoDevices.length === 0 && devices.filter(d => d.kind === 'videoinput').length > 0) {
        throw new Error("VirtualCameraDetected");
      }

      // Windows/Desktop: primary camera = video-1. Mobile: primary camera = video-2 (no overlap on proctor screen)
      const isMobile = isMobileDeviceStable;
      const primaryCameraSlot = isMobile ? 2 : 1;

      // Re-acquisition after tracks ended: release whatever is left of the old streams first
      previousStreams.forEach(s => {
        const stream = s?.stream || s;
        if (stream && stream.getTracks) stream.getTracks().forEach(t => t.stop());
      });

      const activeStreams = [];

      for (let i = 0; i < Math.min(videoDevices.length, targetCameraCount); i++) {
        console.log(`Attempting to start camera ${i + 1}: ${videoDevices[i].label || 'Unknown Label'}`);

        let stream = null;

        // 1. Try with STRICT constraints (Best quality/Specific device)
        try {
          const constraints = {
            video: {
              deviceId: { exact: videoDevices[i].deviceId },
              width: { max: 320 },
              height: { max: 240 },
              frameRate: { max: 10 }
            },
            audio: i === 0 ? { echoCancellation: true, noiseSuppression: true } : false
          };
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          // 2. Fallback: If STRICT fails, try LOOSE constraints
          console.warn(`Camera ${i + 1} strict failed (${err.name}). Trying fallback...`);

          if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError' || err.name === 'NotAllowedError') {
            const fallbackConstraints = {
              video: { frameRate: { max: 10 } },
              audio: i === 0
            };
            stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          } else {
            throw err;
          }
        }

        if (stream) {
          const vTrack = stream.getVideoTracks()[0];

          // Anti-cheat check: Ensure no Canvas or synthetic injection
          const labelLower = vTrack && vTrack.label ? vTrack.label.toLowerCase() : '';
          if (
            (vTrack && vTrack.constructor && vTrack.constructor.name === "CanvasCaptureMediaStreamTrack") ||
            labelLower === '' || labelLower.includes('canvas')
          ) {
            stream.getTracks().forEach(t => t.stop());
            throw new Error("SyntheticStreamDetected");
          }

          // Anti-cheat check: "too perfect" frame rates typical of virtual cameras (logged only)
          if (vTrack && typeof vTrack.getCapabilities === 'function') {
            const caps = vTrack.getCapabilities();
            if (caps && caps.frameRate) {
              const fr = caps.frameRate;
              if ((fr.max === 60 && fr.min === 60) || (fr.max === 30 && fr.min === 30)) {
                console.warn(`⚠️ Suspicious frame rate capabilities detected for ${vTrack.label}:`, fr);
              }
            }
          }

          // Keep the same MediaStream object across re-acquisition so existing <video> elements
          // and the recorder keep receiving frames.
          const prev = previousStreams[i]?.stream || previousStreams[i];
          if (prev instanceof MediaStream) {
            prev.getTracks().forEach(t => prev.removeTrack(t));
            stream.getTracks().forEach(t => prev.addTrack(t));
            stream = prev;
          }

          activeStreams.push(stream);
          const camKind = i === 0
            ? `video-${primaryCameraSlot}`
            : isMobile
              ? `video-${primaryCameraSlot + i}`
              : `video-${i + 1}`;

          console.log(`Device type: ${isMobile ? 'Mobile' : 'Windows'}, Camera ${i + 1} → ${camKind} (slot ${primaryCameraSlot})`);

          if (vTrack) {
            await produceLocal(camKind, vTrack, {
              encodings: CAMERA_ENCODINGS,
              appData: { deviceType: isMobile ? 'mobile' : 'desktop' },
              heartbeat: true,
            });
          }

          if (i === 0) {
            const aTrack = stream.getAudioTracks()[0];
            if (aTrack) await produceLocal('audio', aTrack, { appData: {} });
            if (localVideoRef1.current) localVideoRef1.current.srcObject = stream;
          } else if (localVideoRef2.current) {
            localVideoRef2.current.srcObject = stream;
          }
        }
      }

      localStreamsRef.current = activeStreams;
      if (!unmountedRef.current) setLocalStreams([...activeStreams]);
      return activeStreams;

    } catch (e) {
      console.error("Camera access fatal error:", e);
      if (e.message === 'VirtualCameraDetected') {
        setError("🚫 Virtual cameras (OBS, ManyCam, etc.) are not allowed. Please disable them and use a physical webcam.");
      } else if (e.message === 'SyntheticStreamDetected') {
        setError("🚫 Synthetic or fake camera stream detected. Please use a real hardware camera.");
      } else if (e.name === 'NotAllowedError' && window.isSecureContext === false) {
        setError("Camera blocked: Your browser requires a secure connection (HTTPS) or 'localhost' to access the camera. You are currently using HTTP over an IP address.");
      } else if (e.name === 'NotAllowedError') {
        setError("Camera blocked: Please check your browser permissions and ensure camera access is allowed for this site.");
      } else {
        setError(`Camera Error: ${e.name} - ${e.message}`);
      }
      return [];
    }
  };

  // ---------------- STOP SCREEN SHARE (CLEANUP) ----------------
  const stopScreenShare = async () => {
    unpublishLocal('screen', { stopTrack: true });
    setIsScreenSharing(false);
  };

  /** Live getDisplayMedia video track used for proctoring screen share (null if not sharing). */
  const getScreenShareTrack = useCallback(() => {
    const spec = localProducerSpecsRef.current.get('screen');
    if (spec?.track && isLiveTrack(spec.track)) return spec.track;
    const producer = producersRef.current.get('screen');
    if (producer?.track && isLiveTrack(producer.track)) return producer.track;
    return null;
  }, []);

  // ---------------- START SCREEN SHARE ----------------
  const startScreenShare = async () => {
    try {
      if (isMobileDevice()) {
        console.log("Screen sharing not available on mobile");
        setIsScreenSharing(false);
        return;
      }

      if (!navigator.mediaDevices?.getDisplayMedia) {
        setError("Screen sharing not supported.");
        setIsScreenSharing(false);
        return;
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: "monitor" }, // hint only
          audio: false
        });
      } catch (err) {
        console.warn("Screen sharing constraint failed, falling back to basic video: true", err);
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false
        });
      }

      if (!stream) return;

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();

      // 🔍 VALIDATION STEP (Ensure they shared the entire screen)
      if (settings.displaySurface && settings.displaySurface !== "monitor") {
        setError("❗ Please select 'Entire Screen' to start the exam.");

        track.stop();
        stream.getTracks().forEach(t => t.stop());

        setIsScreenSharing(false);

        // DO NOT auto-retry with setTimeout in Firefox
        const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (!isFirefox && !isSafari) {
          setTimeout(() => startScreenShare(), 500);
        }
        return;
      }

      // ✅ Correct selection
      setIsScreenSharing(true);
      setShowScreenShareButton(false);

      if (track) {
        track.onended = () => {
          stopScreenShare();
        };
        await produceLocal('screen', track, { encodings: SCREEN_ENCODINGS, appData: {} });
      }

    } catch (e) {
      console.error("Screen Share failed:", e);
      if (e.name === 'NotAllowedError' && window.isSecureContext === false) {
        setError("Screen share blocked: Your browser requires a secure connection (HTTPS) or 'localhost' to share your screen. You are currently using HTTP over an IP address.");
      } else if (e.name === 'NotAllowedError') {
        setError("Screen share blocked: Please check your browser permissions.");
      } else {
        setError("Failed to start screen share. Please try again.");
      }
      setIsScreenSharing(false);
    }
  };

  // ---------------- REPLACE PRIMARY CAMERA TRACK (MOBILE SWITCH → STAFF SEES SAME) ----------------
  // Close current producer, wait for server, then produce with new track so staff get a clean stream.
  const replaceFirstCameraTrack = useCallback(async (newTrack) => {
    if (!newTrack || newTrack.kind !== 'video' || newTrack.readyState === 'ended') return;
    const key = isMobileDeviceStable ? 'video-2' : 'video-1';
    if (!sendTransportRef.current) return;

    const producer = producersRef.current.get(key);
    if (producer && !producer.closed && producer.track !== newTrack) {
      const oldTrack = producer.track;
      unpublishLocal(key);
      // The previous camera track is released here (it used to be stopped implicitly by producer.close()).
      if (oldTrack && oldTrack !== newTrack) oldTrack.stop();
      // Brief delay so server and staff process producer-closed before new producer (avoids black/flash)
      await new Promise((r) => setTimeout(r, 200));
    }

    try {
      await produceLocal(key, newTrack, {
        encodings: CAMERA_ENCODINGS,
        appData: { deviceType: isMobileDeviceStable ? 'mobile' : 'desktop' },
        heartbeat: true,
      });
    } catch (err) {
      console.warn('replaceFirstCameraTrack produce failed:', err);
      throw err;
    }
  }, [isMobileDeviceStable, produceLocal, unpublishLocal]);

  // ---------------- STAFF: SUBSCRIBE / PAUSE PER STUDENT ----------------
  const requestStudentStream = useCallback((targetUserId) => {
    if (!socketRef.current) return;
    // Resume consumers we already have (subject to the policy), then pick up any producers we don't have yet.
    applyConsumerPolicyRef.current?.();
    syncProducers(p => String(p.userId) === String(targetUserId), targetUserId);
  }, [syncProducers]);

  const pauseStudentStreams = useCallback((targetUserId) => {
    if (!socketRef.current) return;
    let count = 0;
    consumersRef.current.forEach((consumer) => {
      if (String(consumer.appData?.remoteUserId) === String(targetUserId)) {
        setConsumerPaused(consumer, true);
        count++;
      }
    });
    if (count > 0) console.log(`⏸️ Paused ${count} consumer(s) for off-screen student: ${targetUserId}`);
  }, [setConsumerPaused]);

  const refreshStream = useCallback((targetUserId, kind) => {
    if (!socketRef.current) return;
    Array.from(consumersRef.current.entries()).forEach(([prodId, consumer]) => {
      if (consumer.appData.remoteUserId === targetUserId && (!kind || consumer.appData.kind === kind)) {
        console.log(`🧹 refreshStream: Closing consumer for ${targetUserId} (${consumer.appData.kind})`);
        closeConsumer(prodId);
      }
    });
    setRemoteStreams((prev) => prev.filter((s) => {
      if (kind) return !(s.userId === targetUserId && s.kind === kind);
      return s.userId !== targetUserId;
    }));
    syncProducers(p => p.userId === targetUserId && (!kind || p.kind === kind), targetUserId);
  }, [closeConsumer, syncProducers]);

  // ---------------- EXPORT ----------------
  return {
    isConnected,
    hasJoinedRoom,
    localStreams,
    remoteStreams,
    roomUsers,
    error,
    mediaConnectivityError,
    localVideoRef1,
    localVideoRef2,
    startLocalVideo,
    startScreenShare,
    stopScreenShare,
    getScreenShareTrack,
    isScreenSharing,
    socketRef,
    requestStudentStream,
    pauseStudentStreams,
    refreshStream,
    replaceFirstCameraTrack,
    setConsumerPolicy,
    showScreenShareButton,
    setShowScreenShareButton,
    isMobileDevice: isMobileDeviceStable
  };
};

export default useMediasoup;
